<template>
    <div class="date-range">
        <select class="date-range__item date-range__placeholder" @change="onPlaceholderChange" v-model="placeholder">
            <option v-for="presetKey in Object.keys(currentDateRangePresets)"
                    :value="presetKey">
                {{ currentDateRangePresets[presetKey].title }}
            </option>
        </select>
        <input class="date-range__item" @input="clearPlaceholder" type="date" v-model="start">
        <input class="date-range__item" @input="clearPlaceholder" type="date" v-model="end">
    </div>
</template>

<script>
    import DateHelper from "./DateHelper";

    export default {
        name: "DateRangePicker",
        props: {
            dateRangesPresets: {
                type: Object, default() { return {} }
            },
        },
        mounted() {
            if (this.placeholder !== 'empty') {
                this.end = this.placeholderDates.endDate;
                this.start = this.placeholderDates.startDate;
            }

        },
        beforeUpdate() {
            let data = this.placeholder === 'empty' ? `${this.start} | ${this.end}` : this.placeholderValue
            if (!this.start && !this.end) {
                data = '';
            }
            this.$emit('selected', data);
            this.$emit('clear-selected', {
                dateBegin: this.start,
                dateEnd: this.end,
            })
        },
        data() {
            return {
                start: this.loadDateStartFromStorage(),
                end: this.loadDateEndFromStorage(),
                placeholder: this.loadPlaceholderFromStorage(),
                defaultPresets: {
                    today: {
                        title: 'Сегодня',
                        value: 'today | today',
                        date: () =>  DateHelper.getToday(),
                    },
                    yesterday: {
                        title: 'Вчера',
                        value: '-1 day | -1 day',
                        date: () =>  DateHelper.getYesterday(),
                    },
                    dayBeforeYesterday: {
                        title: 'Позавчера',
                        value: '-2 days | -2 days',
                        date: () =>  DateHelper.getDayBeforeYesterday(),
                    },
                    lastThreeDays: {
                        title: 'Последние 3 дня',
                        value: '-2 days | today',
                        date: () => DateHelper.getLastThreeDays(),
                    },
                    lastSevenDays: {
                        title: 'Последние 7 дней',
                        value: '-6 days | today',
                        date: () => DateHelper.getLastSevenDays(),
                    },
                    lastNineDays: {
                        title: 'Последние 9 дней',
                        value: '-8 days | today',
                        date: () => DateHelper.getLastNineDays(),
                    },
                    thisWeek: {
                        title: 'Эта неделя',
                        value: 'last monday | next sunday',
                        date: () => DateHelper.getCurrentWeek(),
                    },
                    lastWeek: {
                        title: 'Прошлая неделя',
                        value: 'last monday -7 days | next sunday -7 days',
                        date: () => DateHelper.getPreviousWeek(),
                    },
                    lastThirtyDays: {
                        title: 'Последние 30 дней',
                        value: '-30 days | today',
                        date: () => DateHelper.getLastThirtyDays(),
                    },
                    thisMonth: {
                        title: 'Этот месяц',
                        value: 'first day of this month | last day of this month',
                        date: () => DateHelper.getCurrentMonth(),
                    },
                    lastMonth: {
                        title: 'Прошлый месяц',
                        value: 'first day of previous month | last day of previous month',
                        date: () => DateHelper.getPreviousMonth(),
                    },
                    lastThreeMonths: {
                        title: 'Последние 3 месяца',
                        value: '-3 months | today',
                        date: () => DateHelper.getLastThreeMonths(),
                    },
                    lastThreeHundredSixtyFiveDays: {
                        title: 'Последние 365 дней',
                        value: '-365 days | today',
                        date: () => DateHelper.getLastThreeHundredSixtyFiveDays(),
                    },
                    thisYear: {
                        title: 'Этот год',
                        value: 'first day of january | last day of december',
                        date: () => DateHelper.getCurrentYear(),
                    },
                    lastYear: {
                        title: 'Прошлый год',
                        value: '-1 year first day of january | -1 year last day of december',
                        date: () => DateHelper.getPreviousYear(),
                    }
                },
            }
        },
        computed: {
            currentDateRangePresets() {
                const presets = Object.keys(this.dateRangesPresets).length
                    ? this.dateRangesPresets
                    : this.defaultPresets;

                return {
                    empty: {
                        title: '',
                        value: '',
                        date: () => {
                            return {
                                endDate:'',
                                startDate:'',
                            }
                        },
                    },
                    ...presets,
                }
            },
            placeholderValue() {
                return this.currentDateRangePresets[this.placeholder || 'empty'].value;
            },
            placeholderDates() {
                return this.currentDateRangePresets[this.placeholder || 'empty'].date();
            }
        },
        methods: {
            clearPlaceholder() {
                this.placeholder = 'empty';
            },
            clearRange() {
                this.start=''
                this.end=''
                this.clearPlaceholder()
            },
            onPlaceholderChange() {
                if (this.placeholder !== 'empty') {
                    this.start = this.placeholderDates.startDate;
                    this.end = this.placeholderDates.endDate
                } else {
                    this.start = '';
                    this.end = '';
                }
            },
            setPlaceholderDateToInput() {
                if (this.placeholder !== 'empty') {
                    this.start = this.placeholderDates.startDate;
                    this.end = this.placeholderDates.endDate
                }
            },
            savePlaceholderToStorage() {
                localStorage.setItem(`${location.pathname}@vuedatepicker`, this.placeholder);

                localStorage.removeItem(`${location.pathname}@vuedatepicker_start`);
                localStorage.removeItem(`${location.pathname}@vuedatepicker_end`);
            },
            saveDateIntervalToStorage() {
                if (this.start) {
                    localStorage.setItem(`${location.pathname}@vuedatepicker_start`, this.start);
                } else {
                    localStorage.removeItem(`${location.pathname}@vuedatepicker_start`);
                }

                if (this.end) {
                    localStorage.setItem(`${location.pathname}@vuedatepicker_end`, this.end);
                } else {
                    localStorage.removeItem(`${location.pathname}@vuedatepicker_end`);
                }
            },
            loadPlaceholderFromStorage() {
                return localStorage.getItem(`${location.pathname}@vuedatepicker`) ?? 'empty';
            },
            loadDateStartFromStorage() {
                return localStorage.getItem(`${location.pathname}@vuedatepicker_start`) ?? '';
            },
            loadDateEndFromStorage() {
                return localStorage.getItem(`${location.pathname}@vuedatepicker_end`) ?? '';
            },
            deletePlaceholderFromLocalStorage() {
                localStorage.removeItem(`${location.pathname}@vuedatepicker`);
            }
        },
        watch: {
            end() {
                if (this.end < this.start) {
                    this.start = this.end;
                }
                if (this.end > DateHelper.getToday().startDate) {
                    this.end = DateHelper.getToday().startDate;
                }
                this.saveDateIntervalToStorage();
            },
            start() {
                if (this.start > this.end) {
                    this.end = this.start
                }
                this.saveDateIntervalToStorage();
            },
            placeholder() {
                if (this.placeholder === 'empty') {
                    this.deletePlaceholderFromLocalStorage();
                    this.setPlaceholderDateToInput();
                } else {
                    this.savePlaceholderToStorage();
                }
            }
        }
    }
</script>

<style scoped lang="scss">
    .date-range {
        display: flex;
        &__item {
            margin: 0 .2rem 0 0;
        }
        &__placeholder {
            width: auto;
        }
    }
</style>
